import React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Button, Flex } from 'antd';
import BusinessInfo from '../BusinessInfo';
import useForm from '../../../hooks/utility/useForm';
import useApiClient from '../../../hooks/api/useApiClient';
import { useSaveServiceContractMutation } from '../../../store/api-slices';

export const contractFields = [
  'company_name',
  'business_name',
  'business_address',
  'business_zip_code',
  'business_city',
  'contact_person_phone_number',
  'business_tax_id',
  'business_bank_account_holder_name',
  'business_bank_account_number',
];

/**
 * Business-info step — collects the company/business/contact (and, when a setup
 * fee applies, bank) details for the contract. Owns its own form data (separate
 * from the wizard's recipient details) and persists it via the redux save
 * mutation before advancing.
 */
export default function YousignBusinessInfoStep({
  contract,
  hasSetupFee,
  setContract,
  data: providedData,
  update: providedUpdate,
  onBack,
  onNext,
  onPersist,
  isPersisting,
  disabled = false,
}) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Contract:YousignContractFlow' });
  const { t: tSign } = useTranslation(undefined, { keyPrefix: 'Components:Contract:SignStep' });

  const { ServiceContractsManager } = useApiClient();
  const [saveContract, { isLoading: saving }] = useSaveServiceContractMutation();

  // Standalone flow owns its form here (seeded pending-first from `contract`, so
  // a flat field already prefers a staged value); the switch-to-annual wizard
  // instead lifts the form up and passes `data`/`update` in.
  const internalForm = useForm(Object.fromEntries(contractFields.map((field) => [field, contract?.[field]])));
  const data = providedData ?? internalForm.data;
  const update = providedUpdate ?? internalForm.update;

  const saveAndContinue = async () => {
    // Persistence override (switch-to-annual wizard) stages only this step's
    // fields into `pending_changes` instead of committing the whole contract.
    // The parent owns the staged payload; this step just triggers it.
    if (onPersist) {
      const ok = await onPersist();

      if (!ok) {
        return;
      }

      return onNext();
    }

    const { data: responseData, error } = await saveContract({
      apiManager: ServiceContractsManager,
      data,
    });

    if (error) {
      return toast.error(t('toasts.failed_to_save_changes'));
    }

    setContract(responseData);

    return onNext();
  };

  return (
    <div className="mt-medium">
      <BusinessInfo data={data} hasSetupFee={hasSetupFee} fields={contractFields} onChange={update} />
      <Flex justify="space-between" gap="small" className="mt-lg">
        {hasSetupFee && <Button onClick={onBack}>{tSign('buttons.back')}</Button>}
        <Button
          type="primary"
          loading={onPersist ? isPersisting : saving}
          disabled={disabled}
          onClick={saveAndContinue}
          className="ml-auto"
        >
          {t('buttons.save_and_continue')}
        </Button>
      </Flex>
    </div>
  );
}
